import clsx from "clsx"
import {
  Box,
  CardMedia,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  Typography,
  useTheme,
} from "@mui/material"
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded"
import { Head } from "components/Head"
import { Header } from "extensions/iconsiam/components/Header"
import { VenueFilterBar } from "extensions/iconsiam/components/VenueFilterBar"
import { useServiceLocation } from "extensions/iconsiam/hooks/service"
import { PageLayout } from "extensions/iconsiam/layouts/PageLayout"
import { useServiceDetail } from "hooks/service/useServiceDetail"
import { useTranslation } from "providers/i18n"
import { useVenue } from "providers/venue"
import { useParams } from "react-router-dom"
import {
  getFeatureDetail,
  getFeatureIDByPath,
  IAmenityDetail,
} from "utils/feature"
import { HeaderBackButton } from "extensions/iconsiam/components/Header/HeaderBackButton"
import { useMemo } from "react"
import { getIcon } from "utils/icon"
import { EmptyData } from "components/EmptyData"
import { ServiceIcon } from "components/icons"

export const ServiceDetailPage = ({ Layout = PageLayout }) => {
  const { findFeatureById } = useVenue()
  const { t } = useTranslation()
  const params = useParams()
  const { serviceId } = params
  const theme = useTheme()
  const { styleOverrides = {} } = theme.components?.[`ServiceDetailPage`] || {}
  const {
    root,
    locationListItem,
    locationNavigateButton,
    venueLogoWrapper,
    venueLogo: venueLogoStyle,
    locationLogoWrapper,
    locationLogo: locationLogoStyle,
    listNameText,
    floorTextAvatar,
    list,
    emptyDataContainer,
  } = styleOverrides
  const { serviceFilteredByVenue, changeVenueFilter, venueFilter } =
    useServiceDetail(serviceId)
  const { handleClickLocation } = useServiceLocation()

  const ChevronRightIcon = useMemo(
    () => getIcon(theme?.assets?.nextIcon) || <ChevronRightRoundedIcon />,
    [theme]
  )
  const emptyIcon = useMemo(
    () => getIcon(theme?.assets?.empty?.service) || <ServiceIcon />,
    [theme]
  )
  return (
    <>
      <Head title={t("Services")} />
      <Layout
        header={
          <Header
            filterBar={
              <VenueFilterBar
                value={venueFilter}
                onClickVenue={changeVenueFilter}
              />
            }
            backButton={<HeaderBackButton />}
            headerTitle={t(serviceFilteredByVenue?.name)}
          />
        }
      >
        <Box sx={root}>
          {serviceFilteredByVenue?.locations.length > 0 ? (
            <List sx={list}>
              {serviceFilteredByVenue?.locations.map((location, index) => {
                const path = location?.url
                const featureId = getFeatureIDByPath(location?.url)
                const isReferenceID = location.type === "reference-id"
                const feature =
                  isReferenceID && featureId ? findFeatureById(featureId) : null
                const {
                  venueLogo = "",
                  level = "",
                  name: locationName = "",
                } = feature
                  ? (getFeatureDetail(feature) as IAmenityDetail) || {}
                  : {}

                return (
                  <ListItem
                    key={`location-item-${index}`}
                    onClick={() => handleClickLocation(location)}
                    sx={locationListItem}
                    className={clsx(
                      location?.is_highlight
                        ? "ServiceCard-locationListItem--highlight"
                        : ""
                    )}
                    secondaryAction={
                      path &&
                      location?.is_highlight && (
                        <IconButton
                          edge="end"
                          aria-label="navigate"
                          className="ServiceCard-locationNavigateButton"
                          sx={locationNavigateButton}
                        >
                          {ChevronRightIcon}
                        </IconButton>
                      )
                    }
                  >
                    {isReferenceID && (
                      <>
                        {location.image && (
                          <Box sx={locationLogoWrapper}>
                            <CardMedia
                              src={location.image}
                              component="img"
                              sx={locationLogoStyle}
                            />
                          </Box>
                        )}
                        <Typography
                          sx={listNameText}
                          className="ServiceCard-listNameText"
                          variant="subtitle2"
                        >
                          {t(locationName)}
                        </Typography>
                        <Box
                          sx={venueLogoWrapper}
                          className="ServiceCard-venueLogoWrapper"
                        >
                          <CardMedia
                            src={venueLogo}
                            component="img"
                            sx={venueLogoStyle}
                          />
                        </Box>
                        <ListItemAvatar
                          sx={floorTextAvatar}
                          className="ServiceCard-floorTextAvatar"
                        >
                          {t(level)}
                        </ListItemAvatar>
                      </>
                    )}
                    {!isReferenceID && (
                      <>
                        {location.image && (
                          <Box
                            sx={venueLogoWrapper}
                            className="ServiceCard-venueLogoWrapper"
                          >
                            <CardMedia
                              src={location.image}
                              component="img"
                              sx={venueLogoStyle}
                            />
                          </Box>
                        )}
                        <Typography sx={listNameText} variant="subtitle2">
                          {t(location.label)}
                        </Typography>
                      </>
                    )}
                  </ListItem>
                )
              })}
            </List>
          ) : (
            <Box sx={emptyDataContainer}>
              <EmptyData
                icon={emptyIcon}
                primary={t("No Services")}
                secondary={t("Stay tuned for the update")}
              />
            </Box>
          )}
        </Box>
      </Layout>
    </>
  )
}
